import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, LogOut, Lock, Landmark, CheckCircle2, XCircle,
  RefreshCw, User, Ticket, Copy,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import {
  adminListBankTransfers, adminConfirmBankTransfer, adminCancelBankTransfer,
} from '../services/api';

const ADMIN_KEY_STORAGE = 'orosz_admin_key_v1';

const fmtHuDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('hu-HU', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const StatusBadge = ({ status }) => {
  const map = {
    pending:   'bg-amber-500/15 text-amber-300',
    paid:      'bg-emerald-500/15 text-emerald-300',
    cancelled: 'bg-red-500/15 text-red-300',
  };
  const cls = map[status] || 'bg-gray-500/15 text-gray-300';
  return (
    <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${cls}`}>
      {status}
    </span>
  );
};

const LoginScreen = ({ onAuth }) => {
  const [key, setKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!key.trim()) return;
    setSubmitting(true);
    try {
      await adminListBankTransfers(key.trim());
      localStorage.setItem(ADMIN_KEY_STORAGE, key.trim());
      onAuth(key.trim());
      toast.success('Bejelentkezve.');
    } catch (_) {
      toast.error('Érvénytelen admin kulcs.');
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6">
      <form
        onSubmit={handleSubmit}
        data-testid="admin-bt-login-form"
        className="w-full max-w-md bg-gray-800/80 backdrop-blur-md rounded-2xl p-8 border border-gray-700 shadow-2xl"
      >
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 rounded-full bg-yellow-600/20 flex items-center justify-center">
            <Landmark className="text-yellow-500" size={28} />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-center mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Utalások jóváhagyása
        </h1>
        <p className="text-sm text-gray-400 text-center mb-6">Banki utalások áttekintése és aktiválás.</p>
        <Input
          data-testid="admin-bt-key-input"
          type="password"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="ADMIN_API_KEY"
          className="bg-gray-900 border-gray-700 text-white mb-4"
        />
        <Button
          data-testid="admin-bt-login-btn"
          type="submit"
          disabled={submitting || !key.trim()}
          className="w-full bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold"
        >
          {submitting ? <><Loader2 className="mr-2 animate-spin" size={16} /> Belépés…</> : <><Lock className="mr-2" size={16} /> Belépés</>}
        </Button>
      </form>
    </div>
  );
};

const AdminBankTransfersPage = () => {
  useDocumentMeta({ title: 'Utalások | Admin | Orosz Coaching' });
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(ADMIN_KEY_STORAGE) || '');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // order_id being processed
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  const load = useCallback(async () => {
    if (!adminKey) return;
    setLoading(true);
    try {
      const data = await adminListBankTransfers(adminKey);
      setRows(data.transfers || []);
      setCounts(data.counts || {});
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error('Lejárt admin kulcs.');
        localStorage.removeItem(ADMIN_KEY_STORAGE);
        setAdminKey('');
      } else {
        toast.error(err?.response?.data?.detail || 'Betöltési hiba.');
      }
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => { if (adminKey) load(); }, [adminKey, load]);

  const handleConfirm = async () => {
    if (!confirmTarget) return;
    setBusy(confirmTarget.order_id);
    try {
      await adminConfirmBankTransfer(adminKey, confirmTarget.order_id);
      toast.success('Utalás jóváhagyva — a bérlet aktiválva.');
      setConfirmTarget(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sikertelen jóváhagyás.');
    } finally {
      setBusy(null);
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setBusy(cancelTarget.order_id);
    try {
      await adminCancelBankTransfer(adminKey, cancelTarget.order_id, 'admin_cancelled');
      toast.success('Utalás törölve.');
      setCancelTarget(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sikertelen törlés.');
    } finally {
      setBusy(null);
    }
  };

  const copyRef = async (ref) => {
    try { await navigator.clipboard.writeText(ref); toast.success('Közlemény másolva.'); }
    catch { /* ignore */ }
  };

  if (!adminKey) return <LoginScreen onAuth={setAdminKey} />;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-600/20 flex items-center justify-center">
              <Landmark className="text-yellow-500" size={20} />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Utalások jóváhagyása</h1>
              <p className="text-xs text-gray-400">
                {counts.pending || 0} függőben · {counts.paid || 0} jóváhagyva · {counts.cancelled || 0} törölve
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="admin-bt-refresh"
              variant="ghost"
              size="sm"
              onClick={load}
              className="text-gray-300 hover:text-white"
            >
              <RefreshCw size={14} className="mr-1.5" /> Frissítés
            </Button>
            <Button
              data-testid="admin-bt-logout"
              variant="ghost"
              size="sm"
              onClick={() => { localStorage.removeItem(ADMIN_KEY_STORAGE); setAdminKey(''); }}
              className="text-gray-400 hover:text-red-400"
            >
              <LogOut size={14} />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="animate-spin mx-auto text-yellow-500 mb-3" size={28} />
            <p className="text-gray-400">Betöltés…</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-24 text-gray-400">Nincs banki utalás rögzítve.</div>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => (
              <div
                key={r.order_id}
                data-testid={`admin-bt-row-${r.order_id}`}
                className="rounded-xl border border-gray-800 bg-gray-900/60 p-4 md:p-5"
              >
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <StatusBadge status={r.status} />
                      <span className="text-[10px] text-gray-500 font-mono">{r.order_id}</span>
                      <span className="text-[10px] text-gray-500">{fmtHuDate(r.created_at)}</span>
                    </div>
                    <h3 className="text-base md:text-lg font-bold text-white leading-snug" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {r.program_title}{r.tier_label ? ` – ${r.tier_label}` : ''}
                    </h3>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-300">
                        <User size={14} className="text-gray-500" />
                        <span>{r.user?.name || r.billing?.name} · <span className="text-gray-400">{r.user?.email || r.billing?.email}</span></span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-300">
                        <Ticket size={14} className="text-gray-500" />
                        <span>{Number(r.amount_huf || 0).toLocaleString('hu-HU')} Ft · közlemény: <button onClick={() => copyRef(r.reference_code)} className="font-mono font-bold text-yellow-400 hover:underline">{r.reference_code}</button></span>
                      </div>
                    </div>
                    {r.pass && (
                      <div className="mt-2 text-xs text-gray-500">
                        Bérlet: <span className="text-gray-300">{r.pass.total_sessions} alkalom</span>
                        {r.pass.expires_at && r.pass.status === 'active' && (
                          <> · lejár: <span className="text-gray-300">{fmtHuDate(r.pass.expires_at)}</span></>
                        )}
                        {r.pass.status && <> · <span className="text-gray-300">{r.pass.status}</span></>}
                      </div>
                    )}
                  </div>
                  {r.status === 'pending' && (
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        data-testid={`admin-bt-cancel-${r.order_id}`}
                        variant="ghost"
                        size="sm"
                        disabled={busy === r.order_id}
                        onClick={() => setCancelTarget(r)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <XCircle size={14} className="mr-1.5" /> Törlés
                      </Button>
                      <Button
                        data-testid={`admin-bt-confirm-${r.order_id}`}
                        size="sm"
                        disabled={busy === r.order_id}
                        onClick={() => setConfirmTarget(r)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                      >
                        {busy === r.order_id ? <><Loader2 className="animate-spin mr-1.5" size={14} /> …</> : <><CheckCircle2 size={14} className="mr-1.5" /> Fizetés beérkezett</>}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <AlertDialog open={!!confirmTarget} onOpenChange={(v) => !v && setConfirmTarget(null)}>
        <AlertDialogContent data-testid="admin-bt-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Fizetés beérkezett?</AlertDialogTitle>
            <AlertDialogDescription>
              Ellenőrizd az OTP-fiókban az utalást a{' '}
              <strong className="text-yellow-700 font-mono">{confirmTarget?.reference_code}</strong>{' '}
              közleménnyel. A jóváhagyással a bérlet ({confirmTarget?.pass?.total_sessions} alkalom) azonnal aktiválódik és a kliens értesítést kap.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Mégse</AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-bt-confirm-yes"
              onClick={handleConfirm}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Igen, aktiváljuk
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!cancelTarget} onOpenChange={(v) => !v && setCancelTarget(null)}>
        <AlertDialogContent data-testid="admin-bt-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Utalás törlése</AlertDialogTitle>
            <AlertDialogDescription>
              A bérlet <em>cancelled</em> státuszba kerül és a kliens nem tud vele foglalni.
              Ez tipikusan akkor kell, ha az utalás 48 órán belül nem érkezett meg.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Mégse</AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-bt-cancel-yes"
              onClick={handleCancel}
              className="bg-red-600 hover:bg-red-700"
            >
              Igen, töröljük
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminBankTransfersPage;
